import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { IntlService } from "@progress/kendo-react-intl";
import { fieldFormat } from "../../utils/helper";
import PropTypes from "prop-types";

let timeout = null;

/**
 * Edit numeric cell component
 * @module
 * @param {func} onChange - Function invoked on change
 * @param {string} dataItem - Data item
 * @param {string} field - Field
 * @param {string} className - TD Class name
 * @param {number} colSpan - Number of columns which cell expands to
 * @param {object} style - Style
 * @param {number} columnIndex - Column index
 * @param {number} level - Level
 * @param {func} render - Render function
 */
export const editNumericCell = ({ getColumns, isValueChanged }) => {
  const EditNumericCell = (props) => {


    const inputRef = useRef(null);

    const { onChange, dataItem, field, tdProps, isInEdit } = props;
    const [value, setValue] = useState(_.get(dataItem, field.split(".")));


    const handleTimeout = useCallback((e) => {
      onChange({
        dataItem: dataItem,
        field: field,
        syntheticEvent: e,
        value: e.value,
      });

      timeout = null;
    }, [dataItem, field, onChange])

    const handleChange = useCallback((e) => {
      setValue(e.value)

      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }

      timeout = setTimeout(() => {
        handleTimeout(e);
      }, 100);
    }, [handleTimeout])

    useEffect(() => {
      setValue(_.get(dataItem, field.split(".")));
    }, [dataItem, field]);

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (!dataItem.inEdit) {
          return;
        }
        var k = e.keyCode;
        if (e.ctrlKey || k === 8 || k === 9 || k === 13 || k === 27 || (k >= 16 && k <= 20) || (k >= 33 && k <= 40) || (k >= 44 && k <= 46)) {
          if (timeout) {
            clearTimeout(timeout);
            timeout = null;
            handleTimeout(new Event("change"));
            e.stopPropagation();
            setTimeout(() => {
              e.target.dispatchEvent(new KeyboardEvent("keydown", e));
            }, 100);
          }
          return;
        }
      };

      const currentRef = inputRef.current;
      if (currentRef) {
        currentRef.element.addEventListener("keydown", handleKeyDown);
      }

      return () => {
        if (currentRef) {
          currentRef.element.removeEventListener("keydown", handleKeyDown);
        }
      };
    }, [dataItem.inEdit, handleTimeout]);

    const dataValue = value;
    const column = getColumns().find((item) => item.field === field);
    const format = column.format ? fieldFormat(column.format) : "n";
    const formattedValue = new IntlService("sk-SK").format("{0:" + format + "}", dataValue);

    const td = (
      <td
        {...tdProps}
        className={`${tdProps.className} ${isValueChanged && isValueChanged(dataItem, field) ? "k-state-changed" : ""}`}
        tabIndex={0}
      >
        {isInEdit ? (
          <NumericTextBox
            onChange={handleChange}
            value={dataValue}
            defaultValue={column.defaultValue}
            required={column.required}
            readOnly={column.disabled}
            min={column.min === null ? undefined : column.min}
            max={column.max === null ? undefined : column.max}
            step={column.step === null ? undefined : column.step}
            format={format}
            tabIndex={column.tabIndex}
            spinners={column.spinners}
            onBlur={column.onBlur}
            onFocus={column.onFocus}
            ref={inputRef}
            data-testid={"editNumeric"}
          />
        ) : (
          formattedValue
        )}
      </td>
    );
    return td;
  };

  EditNumericCell.propTypes = {
    onChange: PropTypes.func,
    dataItem: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    field: PropTypes.string,
    className: PropTypes.string,
    colSpan: PropTypes.number,
    style: PropTypes.object,
    columnIndex: PropTypes.number,
    level: PropTypes.number,
    render: PropTypes.func,
  };
  return EditNumericCell;
};
